import { createContext, useCallback, useContext, useEffect, useState } from 'react';

// LE SEZIONI CHIUSE SI RICORDANO (utente, 2026-10-15). Chi chiude «Size» non la vuole più vedere a ogni
// area che apre: la ritrova chiusa sulle altre aree e al prossimo accesso. La memoria è PER TIPO DI
// PANNELLO, non per singola cosa — chiuso su un'area, chiuso su tutte —, perché una memoria per oggetto
// è imprevedibile: chiudi qui e di là la ritrovi aperta.
//
// SI SCRIVONO SOLO LE CHIUSE: il default resta «tutto aperto», e una sezione nuova nasce aperta. Vive nel
// browser, come le larghezze delle colonne (`useWorkspacePreferences`): è una comodità di chi guarda, non
// un dato del prodotto. «Reset panel layout» la azzera e riapre tutto, anche le sezioni già a schermo.

const STORAGE_KEY = 'zakeke.panelSections.closed';
const RESET_EVENT = 'zakeke:panel-sections-reset';

const read = (): Set<string> => {
    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
        return new Set(Array.isArray(stored) ? stored.filter((key): key is string => typeof key === 'string') : []);
    } catch {
        return new Set();
    }
};

const write = (closed: Set<string>) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...closed]));
    } catch {
        // Senza il browser che ricorda, la sezione si comporta come prima: aperta di partenza.
    }
};

/** Riapre tutte le sezioni e dimentica quelle chiuse. */
export const resetPanelSections = () => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Niente da dimenticare.
    }
    window.dispatchEvent(new CustomEvent(RESET_EVENT));
};

/**
 * DI CHE PANNELLO È LA SEZIONE: «scene:object», «composer:area». Lo dice chi ospita il pannello — l'Inspector
 * della scena, il pannello del Composer —, così una sezione porta solo il suo id («materials», «size»).
 */
const SectionScopeContext = createContext<string | null>(null);
export const PropertiesPanelSectionScope = SectionScopeContext.Provider;

/**
 * Lo stato di una sezione: ricordato se la sezione ha un id e sta in un pannello che ha uno scope,
 * altrimenti solo per quanto resta a schermo, come prima.
 */
export const useSectionOpen = (id: string | undefined, defaultIsOpen: boolean): [boolean, (next: boolean | ((previous: boolean) => boolean)) => void] => {
    const scope = useContext(SectionScopeContext);
    const key = scope && id ? `${scope}:${id}` : null;
    const [isOpen, setOpenState] = useState(() => (key && read().has(key) ? false : defaultIsOpen));

    // Un pannello riusato per un'altra specie cambia scope: la sezione rilegge il suo stato.
    useEffect(() => {
        if (key) setOpenState(read().has(key) ? false : defaultIsOpen);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => {
        const reopen = () => setOpenState(true);
        window.addEventListener(RESET_EVENT, reopen);
        return () => window.removeEventListener(RESET_EVENT, reopen);
    }, []);

    const setOpen = useCallback(
        (next: boolean | ((previous: boolean) => boolean)) =>
            setOpenState((previous) => {
                const value = typeof next === 'function' ? next(previous) : next;
                if (key) {
                    const closed = read();
                    if (value) closed.delete(key);
                    else closed.add(key);
                    write(closed);
                }
                return value;
            }),
        [key]
    );

    return [isOpen, setOpen];
};
